import { ReactElement } from 'react';

import { DayOfWeek, TimeOfDay } from '@dreryk/egabinet-group-session-frontend-api';

import { BindingCreator, commonTranslationKeys, useTranslate } from '@dreryk/react-common';
import { FilterPanel, Multiselect, Select, StyledFlex } from '@dreryk/react-components';

import {
   GroupSessionsFilterPanelFormBean,
   GroupSessionsFilterPanelSearchParam,
   GroupSessionsFilterPanelStorageKey,
} from '../../GroupSessionsFilterPanel.types';
import { DAYS_OF_WEEK, TIMES_OF_DAY } from '../../GroupSessionsFilterPanel.constants';
import { translationKeys } from '../../../translations';

import {
   createScheduleDayOfWeekTriggerLabelExtractor,
   createScheduleTimesOfDayTriggerLabelExtractor,
   dayOfWeekControllerCodec,
   getDayOfWeekDeselectorsProps,
   timesOfDayControllerCodec,
   useTimesOfDayDeselectorsPropsMapper,
} from './ScheduleController.utils';
import { ScheduleSearchParam } from './Schedule.types';

type ScheduleControllerProps = {
   registerController: BindingCreator<GroupSessionsFilterPanelFormBean, 'scheduleDayOfWeek' | 'scheduleTimesOfDay'>;
};

// eslint-disable-next-line max-lines-per-function
export const ScheduleController = (props: ScheduleControllerProps): ReactElement => {
   const translate = useTranslate();
   const timesOfDayDeselectorsPropsMapper = useTimesOfDayDeselectorsPropsMapper();

   return (
      <StyledFlex gap={'8px'} alignItems={'flex-end'}>
         <FilterPanel.Controller<GroupSessionsFilterPanelFormBean, 'scheduleDayOfWeek', ScheduleSearchParam>
            name={'scheduleDayOfWeek'}
            searchParam={GroupSessionsFilterPanelSearchParam.SCHEDULE_DAY_OF_WEEK}
            storageKey={GroupSessionsFilterPanelStorageKey.SCHEDULE_DAY_OF_WEEK}
            codec={dayOfWeekControllerCodec}
            chipsLabel={translationKeys.schedule}
            getDeselectorsProps={getDayOfWeekDeselectorsProps}
         >
            {({ applyFilter }) => (
               <Select<DayOfWeek>
                  testId={'dayOfWeek'}
                  triggerLabelExtractor={createScheduleDayOfWeekTriggerLabelExtractor(translate)}
                  controller={props.registerController('scheduleDayOfWeek')}
                  onHide={applyFilter}
                  label={translationKeys.schedule}
                  placeholder={translationKeys.day}
                  width={'146px'}
                  compact
               >
                  <>
                     <Select.List<DayOfWeek> items={DAYS_OF_WEEK}>
                        {(item) => (
                           <Select.Item item={item} label={commonTranslationKeys.dayOfWeekMap[item]} testId={item} />
                        )}
                     </Select.List>
                  </>
               </Select>
            )}
         </FilterPanel.Controller>
         <FilterPanel.Controller<GroupSessionsFilterPanelFormBean, 'scheduleTimesOfDay', ScheduleSearchParam>
            name={'scheduleTimesOfDay'}
            searchParam={GroupSessionsFilterPanelSearchParam.SCHEDULE_TIMES_OF_DAY}
            storageKey={GroupSessionsFilterPanelStorageKey.SCHEDULE_TIMES_OF_DAY}
            codec={timesOfDayControllerCodec}
            chipsLabel={translationKeys.schedule}
            getDeselectorsProps={timesOfDayDeselectorsPropsMapper}
         >
            {({ applyFilter }) => (
               <Multiselect<TimeOfDay>
                  testId={'timesOfDay'}
                  triggerLabelExtractor={createScheduleTimesOfDayTriggerLabelExtractor(translate)}
                  controller={props.registerController('scheduleTimesOfDay')}
                  onHide={applyFilter}
                  placeholder={translationKeys.hours}
                  width={'206px'}
                  compact
               >
                  <>
                     <Multiselect.SelectAll
                        selectAllLabel={translationKeys.chooseAll}
                        testId={'chooseAllTimesOfDay'}
                        items={TIMES_OF_DAY}
                     />
                     <Multiselect.List<TimeOfDay> items={TIMES_OF_DAY}>
                        {(item) => (
                           <Multiselect.Item item={item} label={translationKeys.timeOfDayMap[item]} testId={item} />
                        )}
                     </Multiselect.List>
                  </>
               </Multiselect>
            )}
         </FilterPanel.Controller>
      </StyledFlex>
   );
};
